import { FocusTrap } from "@iunctim/component-overlay/Resources/Public/Scripts/features/FocusTrap.js";
import { ScrollLock } from "@iunctim/component-overlay/Resources/Public/Scripts/features/ScrollLock.js";
import { SliderManager } from "@iunctim/component-slider/Resources/Public/Scripts/components/SliderManager.js";
import Swiper from "swiper";
import {
    findEmbeddedVideoBySource,
    isVideoPlaybackStartedMessage,
    pauseVideos,
    videoPlaybackStartedEvent,
} from "@iunctim/component-picture/Resources/Public/Scripts/utilities/VideoPlayback.js";

/**
 * Class to implement the lightbox functionality
 * for a single instance; the application is done
 * via the LightboxManager class
 */
export class Lightbox {
    private openers: NodeListOf<HTMLElement>;
    private closers: NodeListOf<HTMLElement>;

    private focusTrap: FocusTrap;
    private swiper: Swiper | null;
    private isSyncing = false;

    /**
     * Constructor of the Lightbox
     *
     * @param {HTMLElement} lightbox
     */
    public constructor(private lightbox: HTMLElement) {
        // Make use of dependencies
        SliderManager.use();

        this.openers = this.selectOpeners();
        this.closers = this.selectClosers();

        this.focusTrap = new FocusTrap(this.lightbox);
        this.swiper = this.selectSwiper();

        this.useButtons();
        this.useEscapeKey();
        this.useVideoPlayback();
    }

    /**
     * Method to open the lightbox
     *
     * @param {HTMLElement | null} opener
     * @returns {void}
     */
    public open(opener?: HTMLElement): void {
        // Show the lightbox
        this.lightbox.hidden = false;
        ScrollLock.use().lock();

        // Get the focus-trap and enable it
        this.focusTrap.enable(opener);

        // Set aria-expanded to true on all
        // openers for this specific lightbox
        this.openers.forEach((opener) => {
            opener.setAttribute("aria-expanded", "true");
        });

        // Sync the lightbox slider
        // with the element slider
        this.syncSliders(opener);
    }

    /**
     * Method to close the lightbox
     *
     * @returns {void}
     */
    public close(): void {
        // Pause all videos before hiding the lightbox
        pauseVideos(this.lightbox);

        // Hide the lightbox
        this.lightbox.hidden = true;
        ScrollLock.use().unlock();

        // Get the focus-trap and disable it
        this.focusTrap.disable();

        // Set aria-expanded to false on all
        // openers for this specific lightbox
        this.openers.forEach((opener) => {
            opener.setAttribute("aria-expanded", "false");
        });
    }

    /**
     * Ensures that only one video can play inside the lightbox.
     *
     * @returns {void}
     */
    private useVideoPlayback(): void {
        this.lightbox.addEventListener(videoPlaybackStartedEvent, (event) => {
            const player =
                event instanceof CustomEvent &&
                event.detail?.player instanceof Element
                    ? event.detail.player
                    : null;

            pauseVideos(this.lightbox, player);
        });

        // Native media play events do not bubble, so use capture mode.
        this.lightbox.addEventListener(
            "play",
            (event) => {
                const player =
                    event.target instanceof HTMLMediaElement
                        ? event.target
                        : null;

                if (player) pauseVideos(this.lightbox, player);
            },
            true,
        );

        // Observe playback started from YouTube and Vimeo players.
        window.addEventListener("message", (event) => {
            if (!isVideoPlaybackStartedMessage(event.data)) return;

            const player = findEmbeddedVideoBySource(
                this.lightbox,
                event.source,
            );
            if (player) pauseVideos(this.lightbox, player);
        });
    }

    /**
     * Method to handle the opening and closing via buttons
     *
     * @returns {void}
     */
    private useButtons(): void {
        // Handle the openers
        this.openers.forEach((opener) => {
            opener.addEventListener("click", () => this.open(opener));
        });

        // Handle the closers
        this.closers.forEach((closer) => {
            closer.addEventListener("click", () => this.close());
        });
    }

    /**
     * Method to handle the closing via escape-key
     *
     * @returns {void}
     */
    private useEscapeKey(): void {
        // Must be document;
        // doesn't bubble up to this.lightbox
        document.addEventListener("keydown", ({ key }) => {
            if (this.active && key === "Escape") this.close();
        });
    }

    /**
     * Method to wait until the lightbox is truly measureable
     *
     * @returns {Promise<void>}
     */
    private waitUntilVisible(): Promise<void> {
        return new Promise((resolve) => {
            const done = () => resolve();

            // Check if already visible and measurable
            const measurable =
                !this.lightbox.classList.contains("hidden") &&
                this.lightbox.offsetWidth > 0 &&
                this.lightbox.offsetHeight > 0;

            if (measurable) {
                // Request two animation frames to get past layout and first paint
                requestAnimationFrame(() => requestAnimationFrame(done));
                return;
            }

            // Fallback to the next frame
            requestAnimationFrame(done);
        });
    }

    /**
     * Method to get the real index of a swiper instance
     *
     * @param {Swiper} swiper
     * @returns {number}
     */
    private getRealIndex(swiper: Swiper): number {
        // Most reliable with loop is to use realIndex if available
        if (typeof swiper.realIndex === "number") return swiper.realIndex;

        // Fallback via DOM attribute "data-swiper-slide-index"
        const activeElement = swiper.slides[swiper.activeIndex] as
            HTMLElement | undefined;
        const attribute = activeElement?.getAttribute?.(
            "data-swiper-slide-index",
        );
        if (attribute) return parseInt(attribute, 10);

        return swiper.activeIndex ?? 0;
    }

    /**
     * Method to slide to the real index of a swiper instance
     *
     * @param {Swiper} swiper
     * @param {number} realIndex
     * @param {speed} [speed=0]
     */
    private slideToReal(swiper: Swiper, realIndex: number, speed = 0): void {
        const useLoop = !!swiper.params.loop;
        const anySwiper: any = swiper;

        if (useLoop && typeof anySwiper.slideToLoop === "function") {
            anySwiper.slideToLoop(realIndex, speed, false);
        } else {
            swiper.slideTo(realIndex, speed, false);
        }
    }

    /**
     * Method to sync two swiper instances once
     *
     * @param {Swiper} elementSlider
     * @param {Swiper} lightboxSlider
     * @returns {void}
     */
    private syncOnce(elementSlider: Swiper, lightboxSlider: Swiper): void {
        if (this.isSyncing) return;
        this.isSyncing = true;

        // Pause autoplay during synchronization (if enabled)
        const elementAutoWasRunning = !!(
            elementSlider.autoplay && (elementSlider.autoplay as any).running
        );
        const lightboxAutoWasRunning = !!(
            lightboxSlider.autoplay && (lightboxSlider.autoplay as any).running
        );
        elementSlider.autoplay?.stop?.();
        lightboxSlider.autoplay?.stop?.();

        // Temporarily lock movement to avoid event ping-pong
        const prevAllowNext = elementSlider.allowSlideNext;
        const prevAllowPrev = elementSlider.allowSlidePrev;
        elementSlider.allowSlideNext = elementSlider.allowSlidePrev = false;
        lightboxSlider.allowSlideNext = lightboxSlider.allowSlidePrev = false;

        // Update after we know it’s visible
        lightboxSlider.update();

        // Use real index to avoid loop / duplicate weirdness
        const index = this.getRealIndex(elementSlider);
        this.slideToReal(lightboxSlider, index, 0);

        // Restore flags
        elementSlider.allowSlideNext = prevAllowNext;
        elementSlider.allowSlidePrev = prevAllowPrev;
        lightboxSlider.allowSlideNext = lightboxSlider.allowSlidePrev = true;

        // Resume autoplay if it was running
        if (elementAutoWasRunning) elementSlider.autoplay?.start?.();
        if (lightboxAutoWasRunning) lightboxSlider.autoplay?.start?.();

        this.isSyncing = false;
    }

    /**
     * Method to wire the element and the lightbox slider
     *
     * @param {Swiper} elementSlider
     * @param {Swiper} lightboxSlider
     * @return {void}
     */
    private wireTwoWay(elementSlider: Swiper, lightboxSlider: Swiper): void {
        const toLightbox = () => {
            if (!this.active || this.isSyncing) return;
            this.isSyncing = true;
            this.slideToReal(
                lightboxSlider,
                this.getRealIndex(elementSlider),
                0,
            );
            this.isSyncing = false;
        };
        const toElement = () => {
            if (this.isSyncing) return;
            this.isSyncing = true;
            this.slideToReal(
                elementSlider,
                this.getRealIndex(lightboxSlider),
                0,
            );
            this.isSyncing = false;
        };

        elementSlider.on("slideChangeTransitionEnd", toLightbox);
        lightboxSlider.on("slideChangeTransitionEnd", toElement);

        // Clean up when closed
        this.closers.forEach((closer) => {
            closer.addEventListener(
                "click",
                () => {
                    elementSlider.off("slideChangeTransitionEnd", toLightbox);
                    lightboxSlider.off("slideChangeTransitionEnd", toElement);
                },
                {
                    once: true,
                },
            );
        });
    }

    /**
     * Method to sync the lightbox slider with
     * the element slider if any is present
     *
     * @param {HTMLElement | undefined} opener
     * @returns {Promise<void>}
     */
    private async syncSliders(opener?: HTMLElement): Promise<void> {
        if (!opener || !this.swiper) return;

        const requestedIndex = Number.parseInt(
            opener.dataset.lightboxIndex ?? "",
            10,
        );
        if (!Number.isNaN(requestedIndex) && requestedIndex >= 0) {
            const index = Math.min(
                requestedIndex,
                Math.max(0, this.swiper.slides.length - 1),
            );

            // Align synchronously before the browser can paint the newly opened
            // dialog, then repeat after it has become fully measurable.
            this.swiper.update();
            this.slideToReal(this.swiper, index, 0);
            await this.waitUntilVisible();
            this.swiper.update();
            this.slideToReal(this.swiper, index, 0);
            return;
        }

        type SE = HTMLElement & { swiper?: Swiper };
        const a = opener.parentElement?.querySelector(".swiper") as SE;
        const elementSlider = a?.swiper;
        const lightboxSlider = this.swiper;
        if (!elementSlider) return;

        // Ensure the lightbox is visible
        // and measurable before syncing
        await this.waitUntilVisible();

        // Be forgiving to hidden and resized containers
        lightboxSlider.update();
        elementSlider.update?.();

        // Run a one-shot alignment
        this.syncOnce(elementSlider, lightboxSlider);

        // Keep them in lockstep while open
        this.wireTwoWay(elementSlider, lightboxSlider);
    }

    /**
     * Selects the lightbox openers
     *
     * @returns {NodeListOf<HTMLElement>}
     */
    private selectOpeners(): NodeListOf<HTMLElement> {
        return document.querySelectorAll(
            `[aria-controls="${this.lightbox.id}"]`,
        );
    }

    /**
     * Selects the lightbox closers
     *
     * @returns {NodeListOf<HTMLElement>}
     */
    private selectClosers(): NodeListOf<HTMLElement> {
        return this.lightbox.querySelectorAll(`[close]`);
    }

    /**
     * Selects the lightbox swiper
     *
     * @returns {Swiper | null}
     */
    private selectSwiper(): Swiper | null {
        type SE = HTMLElement & { swiper?: Swiper };
        return this.lightbox.querySelector<SE>(".swiper")?.swiper || null;
    }

    /**
     * Helper method to determine whether
     * the lightbox is open or closed
     *
     * @returns {boolean}
     */
    private get active(): boolean {
        return !this.lightbox.hidden;
    }
}
